import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import { Box, Paper, Typography, IconButton, Chip, Checkbox, Divider,
  CircularProgress, Tooltip, TextField, useTheme, Tabs, Tab, Avatar, Button,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Business as CompanyIcon,
  CheckBox as TaskIcon,
  StickyNote2 as StickerIcon,
  Notes as NotesIcon,
  AttachMoney as MoneyIcon,
  OpenInNew as OpenIcon,
  People as PeopleIcon,
  ViewWeek as WeekIcon,
  CalendarMonth as MonthIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const Calendar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { isAdmin } = useAuth();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekData, setWeekData] = useState([]);
  const [calendarViewMode, setCalendarViewMode] = useState('week'); // 'week' или 'month'
  
  // Для админа - табы менеджеров
  const [managers, setManagers] = useState([]);
  const [selectedManagerIndex, setSelectedManagerIndex] = useState(0); // 0 = Все

  // Хелперы для работы с датами
  const isSameDay = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.toDateString() === d2.toDateString();
  };

  const getWeekDays = (date) => {
    const curr = new Date(date);
    const week = [];
    curr.setDate(curr.getDate() - curr.getDay() + 1);
    for (let i = 0; i < 7; i++) {
      week.push(new Date(curr));
      curr.setDate(curr.getDate() + 1);
    }
    return week;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setSelectedDate(newDate);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedDate(newDate);
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    const startDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    for (let i = 0; i < startDayOfWeek; i++) {
      const prevDate = new Date(year, month, -startDayOfWeek + i + 1);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push({ date: new Date(year, month, day), isCurrentMonth: true });
    }
    
    const remainingDays = 7 - (days.length % 7);
    if (remainingDays < 7) {
      for (let i = 1; i <= remainingDays; i++) {
        days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
      }
    }
    
    return days;
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Загрузка списка менеджеров (для админа)
  const fetchManagers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get('/api/users/managers');
      setManagers(response.data);
    } catch (error) {
      console.error('Ошибка загрузки менеджеров:', error);
    }
  }, [isAdmin]);

  // Получить ID выбранного менеджера
  const getSelectedManagerId = () => {
    if (!isAdmin || selectedManagerIndex === 0) return null;
    return managers[selectedManagerIndex - 1]?._id;
  };

  // Загрузка всех карточек пользователя на выбранную дату
  const fetchCardsForDate = useCallback(async (date, managerId = null) => {
    try {
      setLoading(true);
      const dateStr = date.toISOString().split('T')[0];
      let url = `/api/cards/by-date?date=${dateStr}`;
      if (managerId) {
        url += `&managerId=${managerId}`;
      }
      const response = await axios.get(url);
      setCards(response.data);
    } catch (error) {
      console.error('Ошибка загрузки карточек:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Загрузка сводки по неделе или месяцу
  const fetchWeekData = useCallback(async (date, managerId = null, viewMode = 'week') => {
    try {
      let startDate, endDate;
      if (viewMode === 'month') {
        const monthDays = getMonthDays(date);
        startDate = monthDays[0].date.toISOString().split('T')[0];
        endDate = monthDays[monthDays.length - 1].date.toISOString().split('T')[0];
      } else {
        const weekDays = getWeekDays(date);
        startDate = weekDays[0].toISOString().split('T')[0];
        endDate = weekDays[6].toISOString().split('T')[0];
      }
      
      let url = `/api/cards/week-summary?startDate=${startDate}&endDate=${endDate}`;
      if (managerId) {
        url += `&managerId=${managerId}`;
      }
      const response = await axios.get(url);
      setWeekData(response.data);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
    }
  }, []);

  useEffect(() => {
    fetchManagers();
  }, [fetchManagers]);

  useEffect(() => {
    const managerId = getSelectedManagerId();
    fetchCardsForDate(selectedDate, managerId);
    fetchWeekData(selectedDate, managerId, calendarViewMode);
  }, [selectedDate, selectedManagerIndex, managers, calendarViewMode, fetchCardsForDate, fetchWeekData]);

  // Обработка изменения задачи
  const handleToggleTask = async (card, taskId, completed) => {
    try {
      await axios.patch(`/api/cards/${card._id}/tasks/${taskId}`, { completed: !completed });
      fetchCardsForDate(selectedDate);
    } catch (error) {
      console.error('Ошибка обновления задачи:', error);
    }
  };

  // Переход к доске с карточкой
  const handleOpenCard = (card) => {
    if (card.board?.name) {
      navigate(`/kanban/${encodeURIComponent(card.board.name)}`);
    }
  };

  const priorityColors = {
    'низкий': '#a3be8c',
    'средний': '#ebcb8b',
    'высокий': '#d08770',
    'критический': '#bf616a',
  };

  if (loading && cards.length === 0) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} thickness={2} />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 800,
            mb: 3,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #e6edf3 0%, #88c0d0 100%)'
              : 'linear-gradient(135deg, #2e3440 0%, #5e81ac 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          📅 Календарь задач
        </Typography>

        {/* Табы менеджеров для админа */}
        {isAdmin && managers.length > 0 && (
          <Paper
            sx={{
              mb: 3,
              background: isDarkMode ? 'rgba(22, 27, 34, 0.8)' : '#ffffff',
              borderRadius: 3,
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon sx={{ color: '#88c0d0' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Просмотр по менеджерам
              </Typography>
            </Box>
            <Tabs
              value={selectedManagerIndex}
              onChange={(e, newValue) => setSelectedManagerIndex(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 2,
                '& .MuiTabs-indicator': {
                  background: 'linear-gradient(90deg, #88c0d0, #b48ead)',
                  height: 3,
                  borderRadius: 2,
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  minHeight: 56,
                  px: 2,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: isDarkMode ? '#88c0d0' : '#5e81ac',
                  }
                }
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        background: selectedManagerIndex === 0 
                          ? 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)'
                          : isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                        fontSize: '0.7rem',
                      }}
                    >
                      👥
                    </Avatar>
                    <span>Все</span>
                  </Box>
                }
              />
              {managers.map((manager, index) => (
                <Tab
                  key={manager._id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          background: selectedManagerIndex === index + 1 
                            ? 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)'
                            : isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          color: selectedManagerIndex === index + 1 ? '#0d1117' : 'inherit',
                        }}
                      >
                        {manager.firstName[0]}{manager.lastName[0]}
                      </Avatar>
                      <span>{manager.firstName} {manager.lastName}</span>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Paper>
        )}

        {/* Навигация по датам */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            background: isDarkMode ? 'rgba(22, 27, 34, 0.8)' : '#ffffff',
            borderRadius: 3,
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            {/* Переключатель неделя/месяц */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant={calendarViewMode === 'week' ? 'contained' : 'outlined'}
                size="small"
                startIcon={<WeekIcon />}
                onClick={() => setCalendarViewMode('week')}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  ...(calendarViewMode === 'week' && {
                    background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                  })
                }}
              >
                Неделя
              </Button>
              <Button
                variant={calendarViewMode === 'month' ? 'contained' : 'outlined'}
                size="small"
                startIcon={<MonthIcon />}
                onClick={() => setCalendarViewMode('month')}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  ...(calendarViewMode === 'month' && {
                    background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                  })
                }}
              >
                Месяц
              </Button>
            </Box>

            {/* Навигация */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={calendarViewMode === 'month' ? "Предыдущий месяц" : "Предыдущая неделя"}>
                <IconButton onClick={() => calendarViewMode === 'month' ? navigateMonth(-1) : navigateWeek(-1)} size="small">
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
              
              {calendarViewMode === 'week' ? (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {getWeekDays(selectedDate).map((day, index) => {
                    const isSelected = isSameDay(day, selectedDate);
                    const isToday = isSameDay(day, new Date());
                    const dateKey = day.toISOString().split('T')[0];
                    const dayStats = weekData.find(d => d.date === dateKey);
                    
                    return (
                      <Box
                        key={index}
                        onClick={() => setSelectedDate(day)}
                        sx={{
                          minWidth: 80,
                          p: 1.5,
                          borderRadius: 2,
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s',
                          background: isSelected 
                            ? 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)'
                            : isToday 
                              ? isDarkMode ? 'rgba(163, 190, 140, 0.2)' : 'rgba(163, 190, 140, 0.15)'
                              : 'transparent',
                          border: '2px solid',
                          borderColor: isSelected 
                            ? '#88c0d0' 
                            : isToday 
                              ? '#a3be8c' 
                              : 'transparent',
                          '&:hover': {
                            background: isSelected 
                              ? 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)'
                              : isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                          }
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            fontWeight: 600,
                            color: isSelected ? '#0d1117' : 'text.secondary',
                            textTransform: 'uppercase',
                            fontSize: '0.65rem'
                          }}
                        >
                          {day.toLocaleDateString('ru-RU', { weekday: 'short' })}
                        </Typography>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700,
                            color: isSelected ? '#0d1117' : 'text.primary',
                            lineHeight: 1.2
                          }}
                        >
                          {day.getDate()}
                        </Typography>
                        {dayStats && dayStats.cardsCount > 0 && (
                          <Chip 
                            label={dayStats.cardsCount} 
                            size="small"
                            sx={{ 
                              height: 18, 
                              fontSize: '0.65rem',
                              mt: 0.5,
                              background: isSelected ? 'rgba(0,0,0,0.2)' : '#88c0d0',
                              color: isSelected ? '#0d1117' : '#fff',
                            }} 
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 200, textAlign: 'center' }}>
                  {selectedDate.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
                </Typography>
              )}

              <Tooltip title={calendarViewMode === 'month' ? "Следующий месяц" : "Следующая неделя"}>
                <IconButton onClick={() => calendarViewMode === 'month' ? navigateMonth(1) : navigateWeek(1)} size="small">
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <Tooltip title="Сегодня">
                <IconButton onClick={goToToday} color="primary" size="small">
                  <TodayIcon />
                </IconButton>
              </Tooltip>
              
              <TextField
                type="date"
                size="small"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
                sx={{ width: 150 }}
                InputProps={{
                  sx: { borderRadius: 2 }
                }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Месячный вид календаря */}
        {calendarViewMode === 'month' && (
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
              borderRadius: 3,
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              {selectedDate.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
            </Typography>
            
            {/* Заголовки дней недели */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
              {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((dayName) => (
                <Typography 
                  key={dayName} 
                  variant="caption" 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 700, 
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    py: 1
                  }}
                >
                  {dayName}
                </Typography>
              ))}
            </Box>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
              {getMonthDays(selectedDate).map((dayObj, index) => {
                const day = dayObj.date;
                const isCurrentMonth = dayObj.isCurrentMonth;
                const dateKey = day.toISOString().split('T')[0];
                const dayStats = weekData.find(d => d.date === dateKey);
                const isToday = isSameDay(day, new Date());
                const isSelected = isSameDay(day, selectedDate);
                
                return (
                  <Paper
                    key={index}
                    onClick={() => setSelectedDate(day)}
                    sx={{
                      p: 1.5,
                      minHeight: 80,
                      cursor: 'pointer',
                      opacity: isCurrentMonth ? 1 : 0.4,
                      background: isToday 
                        ? isDarkMode ? 'rgba(163, 190, 140, 0.1)' : 'rgba(163, 190, 140, 0.08)'
                        : isDarkMode ? 'rgba(30, 37, 48, 0.5)' : '#f8fafc',
                      border: '2px solid',
                      borderColor: isToday ? '#a3be8c' : isSelected ? '#88c0d0' : 'transparent',
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: isDarkMode 
                          ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                          : '0 4px 12px rgba(0, 0, 0, 0.08)',
                      }
                    }}
                  >
                    <Typography 
                      variant="body1"
                      sx={{ 
                        fontWeight: 700,
                        color: isToday ? '#a3be8c' : 'text.primary',
                        textAlign: 'right',
                        mb: 0.5
                      }}
                    >
                      {day.getDate()}
                    </Typography>
                    
                    {dayStats && dayStats.cardsCount > 0 && (
                      <Chip 
                        label={dayStats.cardsCount} 
                        size="small"
                        icon={<CompanyIcon sx={{ fontSize: '14px !important' }} />}
                        sx={{ 
                          height: 20, 
                          fontSize: '0.65rem',
                          background: '#88c0d0',
                          color: '#fff',
                          '& .MuiChip-icon': { color: '#fff' }
                        }} 
                      />
                    )}
                  </Paper>
                );
              })}
            </Box>
          </Paper>
        )}

        {/* Заголовок с датой */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {selectedDate.toLocaleDateString('ru-RU', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long',
            year: 'numeric'
          })}
        </Typography>

        {/* Список карточек на день */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : cards.length === 0 ? (
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#f8fafc',
              borderRadius: 3,
            }}
          >
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
              📭 На этот день нет запланированных задач
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Добавьте карточки в доски с датой {selectedDate.toLocaleDateString('ru-RU')}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {cards.map((card) => (
              <Paper
                key={card._id}
                sx={{
                  p: 3,
                  background: isDarkMode ? 'rgba(22, 27, 34, 0.8)' : '#ffffff',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
                  borderLeft: `4px solid ${priorityColors[card.priority] || '#88c0d0'}`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateX(4px)',
                    boxShadow: isDarkMode 
                      ? '0 8px 24px rgba(0, 0, 0, 0.4)'
                      : '0 8px 24px rgba(0, 0, 0, 0.12)',
                  }
                }}
              >
                {/* Заголовок карточки */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                    <CompanyIcon sx={{ color: '#88c0d0', fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {card.company?.name || 'Без компании'}
                      </Typography>
                      {card.district && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          📍 {card.district}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {card.dealAmount && (
                      <Chip
                        icon={<MoneyIcon sx={{ fontSize: 16 }} />}
                        label={new Intl.NumberFormat('ru-RU', { 
                          style: 'currency', 
                          currency: 'RUB', 
                          maximumFractionDigits: 0 
                        }).format(card.dealAmount)}
                        size="small"
                        sx={{
                          background: 'rgba(163, 190, 140, 0.15)',
                          border: '1px solid rgba(163, 190, 140, 0.3)',
                          color: '#a3be8c',
                          fontWeight: 700,
                        }}
                      />
                    )}
                    <Chip
                      label={card.priority}
                      size="small"
                      sx={{
                        background: `${priorityColors[card.priority]}30`,
                        border: `1px solid ${priorityColors[card.priority]}`,
                        color: priorityColors[card.priority],
                        fontWeight: 600,
                      }}
                    />
                    <Tooltip title="Открыть в доске">
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenCard(card)}
                        sx={{ 
                          color: '#88c0d0',
                          '&:hover': { background: 'rgba(136, 192, 208, 0.1)' }
                        }}
                      >
                        <OpenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Контактная информация */}
                {(card.company?.phone || card.company?.email) && (
                  <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                    {card.company?.phone && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        📞 {card.company.phone}
                      </Typography>
                    )}
                    {card.company?.email && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        ✉️ {card.company.email}
                      </Typography>
                    )}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Задачи */}
                {card.tasks && card.tasks.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TaskIcon sx={{ color: '#a3be8c', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Задачи ({card.tasks.filter(t => t.completed).length}/{card.tasks.length})
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 3.5 }}>
                      {card.tasks.map((task) => (
                        <Box 
                          key={task._id} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            py: 0.5,
                            '&:hover': {
                              background: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                              borderRadius: 1,
                            }
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={task.completed}
                            onChange={() => handleToggleTask(card, task._id, task.completed)}
                            sx={{ 
                              p: 0.5,
                              color: '#88c0d0',
                              '&.Mui-checked': { color: '#a3be8c' }
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: task.completed ? 'line-through' : 'none',
                              color: task.completed ? 'text.secondary' : 'text.primary',
                              flex: 1,
                            }}
                          >
                            {task.text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Стикеры */}
                {card.stickers && card.stickers.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <StickerIcon sx={{ color: '#ebcb8b', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Стикеры
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 3.5 }}>
                      {card.stickers.map((sticker) => (
                        <Chip
                          key={sticker._id}
                          label={sticker.text}
                          size="small"
                          sx={{
                            background: `${sticker.color}30`,
                            border: `1px solid ${sticker.color}`,
                            color: sticker.color,
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Заметки */}
                {card.notes && card.notes.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <NotesIcon sx={{ color: '#d08770', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Заметки
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 3.5 }}>
                      {card.notes.map((note) => (
                        <Box 
                          key={note._id}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            background: 'rgba(235, 203, 139, 0.1)',
                            borderRadius: 2,
                            borderLeft: '3px solid #ebcb8b',
                          }}
                        >
                          <Typography variant="body2">
                            {note.text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Информация о доске */}
                {card.board?.name && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      📋 Доска: <strong>{card.board.name}</strong>
                    </Typography>
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default Calendar;

